/**
 * BusinessLangContext — dashboard language for the Business Owner dashboard.
 *
 * This is a DASHBOARD language, completely separate from the client-facing
 * (restaurant menu / platform home) language. An owner whose phone/UI is Persian
 * can keep the dashboard in English, or vice-versa.
 *
 * Storage: per-account on the server (BusinessOwner.dashboardLang +
 * dashboardTranslations). 'en' uses the built-in English defaults below; any
 * other language uses dashboardTranslations (a flat key→string map produced by
 * the auto-translate action, API→AI). Direction (rtl/ltr) follows the language.
 */
import React, { createContext, useContext } from 'react';
import { LANGUAGES } from './LanguageContext';
import { useUnifiedUi } from '../lang/uiStore';

// ─── Business dashboard string catalogue (grouped for the translate UI) ───────
export const BIZ_UI_SECTIONS = [
  { id: 'nav', label: '🧭 Navigation', keys: [
    { key: 'biz_overview',     default: 'Overview'        },
    { key: 'biz_restaurants',  default: 'My Restaurants'  },
    { key: 'biz_analytics',    default: 'Analytics'       },
    { key: 'biz_grow',         default: 'Grow'            },
    { key: 'biz_plans',        default: 'Plans & Billing' },
    { key: 'biz_grow_title',     default: 'Grow your business' },
    { key: 'biz_grow_sub',       default: 'Your trial status and your referral rewards in one place.' },
    { key: 'biz_grow_trial',     default: 'Free trial' },
    { key: 'biz_grow_active',    default: 'Active subscription' },
    { key: 'biz_grow_free',      default: 'Free menu' },
    { key: 'biz_grow_expired',   default: 'Trial ended' },
    { key: 'biz_grow_days_left', default: 'days left' },
    { key: 'biz_ref_title',      default: 'Invite restaurants, earn free time' },
    { key: 'biz_ref_desc',       default: 'Share your link. When a restaurant signs up with it, you get {reward} free days and they get {friend} extra trial days.' },
    { key: 'biz_ref_copy',       default: 'Copy link' },
    { key: 'biz_ref_copied',     default: 'Referral link copied ✓' },
    { key: 'biz_ref_friends',    default: 'Friends joined' },
    { key: 'biz_ref_bonus_days', default: 'Bonus days earned' },
    { key: 'biz_ref_code',       default: 'Your code' },
    { key: 'biz_account',      default: 'Account'         },
    { key: 'biz_payout',       default: 'Payouts'         },
    { key: 'biz_payout_title', default: 'Payout details'  },
    { key: 'biz_payout_sub',   default: 'Where we send your money once online payments go live. Choose your country, then enter your bank details.' },
    { key: 'biz_payout_country', default: 'Country' },
    { key: 'biz_payout_save',  default: 'Save payout details' },
    { key: 'biz_payout_saved', default: 'Payout details saved ✓' },
    { key: 'biz_payout_soon',  default: '💳 Online payments are coming soon — add your details now so you’re ready on day one.' },
    { key: 'biz_plan_locked',  default: 'Upgrade to unlock' },
    { key: 'biz_plan_included',default: 'Included in your plan' },
    { key: 'biz_language',     default: 'Language'        },
    { key: 'biz_logout',       default: 'Sign Out'        },
  ]},
  { id: 'overview', label: '📊 Overview', keys: [
    { key: 'biz_welcome',        default: 'Welcome back'         },
    { key: 'biz_total_rest',     default: 'Restaurants'          },
    { key: 'biz_total_orders',   default: 'Total Orders'         },
    { key: 'biz_total_revenue',  default: 'Total Revenue'        },
    { key: 'biz_active',         default: 'Active'               },
    { key: 'biz_inactive',       default: 'Inactive'             },
  ]},
  { id: 'restaurants', label: '🍽️ Restaurants', keys: [
    { key: 'biz_rest_title',     default: 'My Restaurants'       },
    { key: 'biz_rest_sub',       default: 'Manage your venues, branches and menus' },
    { key: 'biz_new_rest',       default: '+ New Restaurant'     },
    { key: 'biz_create_rest',    default: '+ Create Restaurant'  },
    { key: 'biz_no_rest',        default: 'No restaurants yet'   },
    { key: 'biz_no_rest_sub',    default: 'Create your first restaurant to get started' },
    { key: 'biz_add_rest',       default: '+ Add Restaurant'     },
    { key: 'biz_open_admin',     default: 'Open Admin Panel'     },
    { key: 'biz_view_site',      default: 'View Site'            },
    { key: 'biz_edit_info',      default: 'Edit Info'            },
    { key: 'biz_admin_creds',    default: 'Admin Credentials'    },
    { key: 'biz_branches',       default: 'Branches'             },
    { key: 'biz_delete',         default: 'Delete'               },
    { key: 'biz_your_site',      default: 'Your site'            },
  ]},
  { id: 'analytics', label: '📈 Analytics', keys: [
    { key: 'biz_analytics_title', default: 'Analytics & AI Insights' },
    { key: 'biz_analytics_sub',   default: 'Real-time data · AI analyst · Full audit trail · Export CSV' },
    { key: 'biz_analytics_empty', default: 'Add a restaurant first to see analytics.' },
  ]},
  { id: 'subscription', label: '💎 Subscription', keys: [
    { key: 'biz_sub_title',      default: 'Subscription & Billing' },
    { key: 'biz_sub_sub',        default: 'Choose the plan that fits your business' },
    { key: 'biz_current_plan',   default: 'Current Plan'         },
    { key: 'biz_upgrade',        default: 'Upgrade'              },
  ]},
  { id: 'account', label: '👤 Account', keys: [
    { key: 'biz_account_title',  default: 'Account Settings'     },
    { key: 'biz_business_info',  default: 'Business Info'        },
    { key: 'biz_change_pass',    default: 'Change Password'      },
    { key: 'biz_confirm_pass',   default: 'Confirm Password'     },
    { key: 'biz_update_pass',    default: 'Update Password'      },
    { key: 'biz_owner_name',     default: 'Owner Name'           },
    { key: 'biz_email',          default: 'Email'                },
    { key: 'biz_phone',          default: 'Phone'                },
    { key: 'biz_save',           default: 'Save Changes'         },
    { key: 'biz_current_pass',   default: 'Current Password'     },
    { key: 'biz_new_pass',       default: 'New Password'         },
  ]},
  { id: 'language', label: '🌐 Language', keys: [
    { key: 'biz_lang_title',     default: 'Dashboard Language'   },
    { key: 'biz_lang_sub',       default: 'Choose the language for your dashboard. This is separate from your restaurants’ menu language.' },
    { key: 'biz_lang_auto',      default: 'Auto-translate dashboard' },
    { key: 'biz_lang_saving',    default: 'Translating…'         },
    { key: 'biz_lang_save',      default: 'Save Language'        },
  ]},
  { id: 'modals', label: '🗂️ Forms & Modals', keys: [
    // shared
    { key: 'biz_cancel',          default: 'Cancel'                  },
    { key: 'biz_status',          default: 'Status'                  },
    { key: 'biz_tagline',         default: 'Tagline'                 },
    { key: 'biz_address',         default: 'Address'                 },
    { key: 'biz_hours',           default: 'Hours'                   },
    { key: 'biz_cuisine',         default: 'Cuisine'                 },
    { key: 'biz_brand_color',     default: 'Brand Color'             },
    // add restaurant
    { key: 'biz_new_restaurant',  default: 'New Restaurant'          },
    { key: 'biz_subdomain_url',   default: 'Subdomain (URL)'         },
    { key: 'biz_cuisine_type',    default: 'Cuisine Type'            },
    { key: 'biz_admin_email',     default: 'Admin Email'             },
    { key: 'biz_admin_password',  default: 'Admin Password'          },
    { key: 'biz_min6',            default: 'Min 6 characters'        },
    { key: 'biz_summary',         default: 'Summary'                 },
    { key: 'biz_opening_hours',   default: 'Opening Hours'           },
    // edit restaurant
    { key: 'biz_edit_restaurant', default: 'Edit Restaurant'         },
    { key: 'biz_restaurant_name', default: 'Restaurant Name'         },
    // branches
    { key: 'biz_branch_name',     default: 'Branch Name'             },
    { key: 'biz_branch_slug',     default: 'Branch Slug'             },
    { key: 'biz_branch_address',  default: 'Branch address'          },
    { key: 'biz_branch_phone',    default: 'Branch phone'            },
    { key: 'biz_main',            default: 'Main'                    },
    { key: 'biz_edit',            default: 'Edit'                    },
    { key: 'biz_same_subdomain',  default: 'Same subdomain'          },
    { key: 'biz_branch_selector', default: 'Branch selector'         },
    { key: 'biz_branch_sel_desc', default: 'Landing page shows one button per active branch' },
    { key: 'biz_own_menu',        default: 'Own menu'                },
    { key: 'biz_landing_preview', default: 'Landing Page Preview'    },
    { key: 'biz_enter_menu',      default: 'Enter the Menu'          },
    // admin creds
    { key: 'biz_admin_login_creds', default: 'Admin Login Credentials' },
    { key: 'biz_new_admin_email',   default: 'New Admin Email'         },
    { key: 'biz_new_admin_pass',    default: 'New Admin Password'      },
    { key: 'biz_leave_blank_keep',  default: 'Leave blank to keep current' },
  ]},
  { id: 'toasts', label: '🔔 Messages', keys: [
    { key: 'biz_t_deleted',        default: 'deleted'              },
    { key: 'biz_t_account_updated',default: 'Account updated ✓'    },
    { key: 'biz_t_creds_updated',  default: 'Admin credentials updated ✓' },
    { key: 'biz_t_branch_created', default: 'Branch created ✓'     },
    { key: 'biz_t_branch_deleted', default: 'Branch deleted'       },
    { key: 'biz_t_branch_updated', default: 'Branch updated ✓'     },
    { key: 'biz_t_contact_sales',  default: 'Contact sales@nowshin.ir to upgrade' },
    { key: 'biz_t_enter_passwords',default: 'Enter current and new password' },
    { key: 'biz_t_toggle_failed',  default: 'Failed to toggle branch' },
    { key: 'biz_t_pass_min6',      default: 'New password must be at least 6 characters' },
    { key: 'biz_t_pass_mismatch',  default: 'New passwords do not match' },
    { key: 'biz_t_rest_created',   default: 'New restaurant created successfully ✓' },
    { key: 'biz_t_pass_updated',   default: 'Password updated ✓'   },
    { key: 'biz_t_rest_updated',   default: 'Restaurant updated ✓' },
    { key: 'biz_t_update_failed',  default: 'Update failed'        },
    { key: 'biz_t_create_failed',  default: 'Failed to create restaurant' },
    { key: 'biz_t_lang_en',        default: 'Dashboard set to English ✓' },
    { key: 'biz_t_lang_changed',   default: 'Dashboard language updated ✓' },
    { key: 'biz_t_lang_failed',    default: 'Could not change language' },
  ]},
];

export const BIZ_UI_DEFAULTS = {};
BIZ_UI_SECTIONS.forEach(s => s.keys.forEach(k => { BIZ_UI_DEFAULTS[k.key] = k.default; }));

const BusinessLangContext = createContext(null);

export function BusinessLangProvider({ children, lang = 'fa', translations = {} }) {
  const dir = (LANGUAGES[lang]?.dir) || 'ltr';
  // Owner-managed unified store (from /api/i18n) — one source shared with the
  // SSR pages and the other dashboards. Overlays the in-code defaults.
  const unified = useUnifiedUi(lang);

  // bt(key) — dashboard translator. English default unless an override exists.
  const bt = (key) => {
    if (lang && lang !== 'en') {
      const override = translations?.[key];
      if (override) return override;
    }
    if (unified[key]) return unified[key];
    return BIZ_UI_DEFAULTS[key] || key;
  };

  return (
    <BusinessLangContext.Provider value={{ bt, lang, dir }}>
      {children}
    </BusinessLangContext.Provider>
  );
}

export const useBusinessT = () => {
  const ctx = useContext(BusinessLangContext);
  if (!ctx) return { bt: (k) => BIZ_UI_DEFAULTS[k] || k, lang: 'fa', dir: 'rtl' };
  return ctx;
};
